import { RefObject, useEffect, useState } from "react";
import { useHasAnyFavouriteTemplates } from "src/store/favouriteTemplates/hooks/useSelectors";
import { getModulesWidths } from "../utils";
import useNavModulesContainerWidth from "./useNavModulesContainerWidth";

interface ReturnType {
  visibleModuleIds: number[];
  visibleLeftArrow: boolean;
  visibleRightArrow: boolean;
  leftArrowAction(): void;
  rightArrowAction(): void;
}

/**
 * Get sliced modules for current viewport and actions for arrows used
 * for moving between modules in nav
 */
const useNavModulesAndItsActions = (
  moduleIds: number[],
  divNavRef: RefObject<HTMLDivElement>
): ReturnType => {
  const [modulesPaging, setModulesPaging] = useState<number[]>([]);
  const [activePage, setActivePage] = useState(0);
  const modulesContainerWidth = useNavModulesContainerWidth();
  const hasAnyFavouriteTemplates = useHasAnyFavouriteTemplates();

  const resetPagination = (): void => {
    setActivePage(0);
    setModulesPaging([]);
  };

  const modulesReduceFnc = (
    totalWidth: number,
    currModulWidth: number,
    currModulIndex: number,
    defaultArr: number[]
  ): number => {
    // need to add arrows width to containers width to know if its overall width overflowing container
    const newTotalWidth = totalWidth + currModulWidth;
    // i need to know width of elements which will suite to width of container
    // if next element will overflow, i have to add him to new page and return
    // modular result of dividing
    const overflowingWidth = newTotalWidth % modulesContainerWidth;

    // adding index, where one page of modules end
    if (overflowingWidth !== newTotalWidth) {
      setModulesPaging((prevState) => [...prevState, currModulIndex]);
    }

    // if its last index, have to add that index to paging bcs of slice method
    if (currModulIndex === defaultArr.length - 1) {
      setModulesPaging((prevState) => [...prevState, currModulIndex + 1]);
      return 0;
    }

    // remove arrows width from total sum bcs dont want to add to every iteration
    return overflowingWidth;
  };

  const createModulesPaging = (): void => {
    if (!divNavRef?.current) return;

    const modulesWidths = getModulesWidths(divNavRef.current);
    modulesWidths.reduce(modulesReduceFnc, 0);
  };

  useEffect(() => {
    resetPagination();
    createModulesPaging();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleIds, modulesContainerWidth]);

  const getVisibleModulesIds = (): number[] => {
    if (activePage === 0) return moduleIds.slice(0, modulesPaging[activePage]);

    return moduleIds.slice(
      modulesPaging[activePage - 1],
      modulesPaging[activePage]
    );
  };

  const onLeftArrowClick = (): void => {
    if (activePage === 0) return;
    setActivePage((prevState) => prevState - 1);
  };

  const onRightArrowClick = (): void => {
    if (activePage === modulesPaging.length - 1) return;
    setActivePage((prevState) => prevState + 1);
  };

  return {
    visibleModuleIds: getVisibleModulesIds(),
    visibleLeftArrow: activePage > 0,
    visibleRightArrow: activePage < modulesPaging.length - 1,
    leftArrowAction: onLeftArrowClick,
    rightArrowAction: onRightArrowClick,
  };
};

export default useNavModulesAndItsActions;
